import 'moment/locale/ko';

import type { NextPage } from 'next';
import type { AppProps } from 'next/app';

import moment from 'moment';
import { ThemeProvider, Global } from '@emotion/react';
import Fallback from '@messenger/components/Fallback';
import useUserInfo from '@messenger/hooks/useUserInfo';
import { SendbirdProvider } from '@messenger/providers';
import { DetermineMobileProvider, WebViewProvider } from '@myrealtrip/device-detect';
import { MarketingAutoOpen } from '@myrealtrip/marketing-log';
import { DEFAULT_THEME, global as webUiGlobal } from '@myrealtrip/web-ui';
import { Hydrate } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { RecoilRoot } from 'recoil';

import ErrorBoundary from '~components/ErrorBoundary';
import AppMeta from '~components/metas/AppMeta';
import QueryClientProvider from '~contexts/QueryClientProvider';
import globalStyle from '~styles/globals';

moment.locale('ko');

type EnhancedAppProps = AppProps & {
  Component: NextPage;
  pageProps: Record<string, unknown>;
};

// SendbirdProvider 는 인증 사용자에 한해 항상 마운트한다.
// 한 번 connect 한 SDK 연결을 유지하고, 채팅 페이지(/channels, /channels/[channelId])에서만
// foreground 상태로, 그 외 페이지에서는 background 상태로 전환하는 정책 (PresenceController 참고).
function SendbirdWrapper({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const channelId = router.query.channelId as string | undefined;
  const { userId, accessToken, isLoading, hasError } = useUserInfo();

  if (isLoading) {
    return <Fallback />;
  }

  if (hasError || !userId) {
    return <>{children}</>;
  }

  return (
    <SendbirdProvider userId={userId} accessToken={accessToken} channelId={channelId}>
      {children}
    </SendbirdProvider>
  );
}

export default function CustomApp({ Component, pageProps }: EnhancedAppProps) {
  const { isMobile, webViewInfo, ...restPageProps } = pageProps;

  return (
    <>
      <AppMeta />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <QueryClientProvider>
        <Hydrate state={pageProps.dehydratedState}>
          <RecoilRoot>
            <WebViewProvider defaultWebViewInfo={webViewInfo as any}>
              <DetermineMobileProvider defaultMobile={Boolean(isMobile)}>
                <ThemeProvider theme={DEFAULT_THEME}>
                  <Global styles={[webUiGlobal, globalStyle]} />
                  <MarketingAutoOpen />
                  <ErrorBoundary>
                    <SendbirdWrapper>
                      <Component {...restPageProps} />
                    </SendbirdWrapper>
                  </ErrorBoundary>
                </ThemeProvider>
              </DetermineMobileProvider>
            </WebViewProvider>
          </RecoilRoot>
        </Hydrate>
      </QueryClientProvider>
    </>
  );
}
